import { useEffect, useState } from 'react';
import { BookOpen, Copy } from 'lucide-react';

/**
 * API documentation — the "bruh, docs?" answer.
 *
 * Public page (no login): everything needed to build on Gauntlet's payment
 * request API, verify webhooks, and embed the widget.
 */
export function DocsPage() {
  const [origin, setOrigin] = useState('https://your-gauntlet');

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  return (
    <div className="min-h-dvh bg-void px-4 py-10 text-ink">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <BookOpen size={19} className="text-brass" />
            Gauntlet API
          </h1>
          <p className="text-sm text-ink-dim">
            Payment requests, webhooks and the widget — for people with no bank and no
            payment processor. Base URL: <Code>{origin}</Code>
          </p>
        </header>

        <Section title="1. Authentication">
          <p>
            Everything below (except the public endpoints) needs either a session cookie or a
            personal API token:
          </p>
          <CodeBlock>{`Authorization: Bearer gt_...`}</CodeBlock>
          <p>
            Create a token in <strong>Settings → API</strong>, or with the API itself:
          </p>
          <CodeBlock>{`curl -X POST ${origin}/api/tokens \\
  -H "Authorization: Bearer <your-session-or-token>" \\
  -H "content-type: application/json" \\
  -d '{"label": "my-website"}'
# -> { "token": "gt_...", "row": { "id": 1, "label": "my-website", ... } }`}</CodeBlock>
          <p className="text-ink-faint">
            The token is shown exactly once and hashed on the server. Tokens can create and
            read requests — they can never unlock, sign or spend, even with the password.
            Revoke with <InlineCode>DELETE /api/tokens/&lt;id&gt;</InlineCode>.
          </p>
        </Section>

        <Section title="2. Payment requests">
          <p>
            A request is a shareable "pay me" link. <strong>Single</strong> mode is one fixed
            invoice; <strong>multi</strong> mode is a goal many people pay toward. Every
            request gets its own freshly derived address.
          </p>

          <Sub>Create</Sub>
          <CodeBlock>{`curl -X POST ${origin}/api/requests \\
  -H "Authorization: Bearer gt_..." -H "content-type: application/json" \\
  -d '{
    "accountId": 1,
    "mode": "single",            // or "multi" for a goal
    "title": "Pi setup",
    "note": "Raspberry Pi configuration",
    "chainId": "bitcoin",
    "amount": "100000",          // base units (sats for bitcoin)
    "expiryDays": 7,             // optional
    "webhookUrl": "https://your-site.com/hook",   // optional pair
    "webhookSecret": "a-long-random-string-16+"
  }'
# -> { "request": { "id": 1, "slug": "Ab3xY9Z0Q1", "address": "bc1q...", ... } }`}</CodeBlock>

          <Sub>Public page &amp; status</Sub>
          <CodeBlock>{`curl ${origin}/api/public/requests/Ab3xY9Z0Q1
# -> { "title": "Pi setup", "mode": "single", "amount": "100000",
#      "address": "bc1q...", "uri": "bitcoin:bc1q...?amount=0.001",
#      "status": "open", "confirmedTotal": "0", "pendingTotal": "0",
#      "payments": [ { "txid": "...", "amount": "100000",
#                      "status": "confirmed", "confirmations": 1 } ] }`}</CodeBlock>
          <p className="text-ink-faint">
            No auth needed — this is what the share link uses. Status flips the moment a
            payment is broadcast, before confirmation. The human page is at{' '}
            <InlineCode>{origin}/r/Ab3xY9Z0Q1</InlineCode>.
          </p>

          <Sub>List, detail, cancel, delete</Sub>
          <CodeBlock>{`GET    ${origin}/api/requests                  # all of yours, with live status
GET    ${origin}/api/requests/1              # one, with live status
PATCH  ${origin}/api/requests/1              # {"action":"cancel"} — close it
DELETE ${origin}/api/requests/1              # remove it permanently`}</CodeBlock>
        </Section>

        <Section title="3. Webhooks">
          <p>
            When a request has a <InlineCode>webhookUrl</InlineCode> +{' '}
            <InlineCode>webhookSecret</InlineCode>, Gauntlet POSTs a JSON payload on every
            event. Each payload is signed: header{' '}
            <InlineCode>X-Gauntlet-Signature</InlineCode> = HMAC-SHA256 of the raw body using
            the secret as the key. Always verify it.
          </p>
          <CodeBlock>{`{ "event": "payment_detected",      // broadcast, unconfirmed
  "requestId": 1, "slug": "Ab3xY9Z0Q1", "mode": "single",
  "title": "Pi setup", "chainId": "bitcoin", "amount": "100000",
  "address": "bc1q...", "txid": "...", "txAmount": "100000", "ts": 1785626642785 }

events: payment_detected | payment_confirmed | completed`}</CodeBlock>
          <Sub>Verify (Node)</Sub>
          <CodeBlock>{`import { createHmac, timingSafeEqual } from 'node:crypto';

export function verify(req, secret) {
  const signature = req.headers['x-gauntlet-signature'];
  const body = JSON.stringify(req.body);
  const expected = createHmac('sha256', secret).update(body).digest('hex');
  const a = Buffer.from(signature ?? ''), b = Buffer.from(expected);
  return a.length === b.length && timingSafeEqual(a, b);
}`}</CodeBlock>
          <p className="text-ink-faint">
            Delivery is retried with backoff up to six times. Ship the goods only on{' '}
            <InlineCode>payment_confirmed</InlineCode> (or <InlineCode>completed</InlineCode>).
          </p>
        </Section>

        <Section title="4. Widget">
          <p>One script tag renders a live pay card on any website:</p>
          <CodeBlock>{`<script src="${origin}/widget.js" data-request="Ab3xY9Z0Q1" defer></script>`}</CodeBlock>
          <p>
            The widget polls the public endpoint and fires a{' '}
            <InlineCode>gauntlet:payment</InlineCode> CustomEvent with the latest state on
            every change — react to it for the "thank you" screen or to ship goods:
          </p>
          <CodeBlock>{`window.addEventListener('gauntlet:payment', (e) => {
  if (e.detail.status === 'paid') document.body.classList.add('paid');
});`}</CodeBlock>
          <p className="text-ink-faint">
            The payer always pays from their own wallet (QR / BIP-21). The widget only
            receives, tracks and notifies — Gauntlet never moves a stranger's money.
          </p>
        </Section>

        <Section title="5. Errors">
          <CodeBlock>{`{ "error": { "code": "VALIDATION", "message": "Amount must be a positive number" } }

codes: VALIDATION (400) | NOT_FOUND (404) | CONFLICT (409)
       | UNAUTHORIZED (401) | PROVIDER_ERROR (502) | SIGN_FAILED (500)`}</CodeBlock>
        </Section>

        <footer className="pt-4 text-xs text-ink-faint">
          <CopyHint />
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold tracking-tight text-brass">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-ink-dim">{children}</div>
    </section>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return <p className="pt-1 text-xs font-semibold uppercase tracking-wider text-ink-faint">{children}</p>;
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="addr rounded bg-abyss px-1.5 py-0.5 text-xs">{children}</code>
  );
}

function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="addr rounded bg-abyss px-1 py-0.5 text-xs">{children}</code>
  );
}

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg border border-hairline bg-abyss p-3 text-xs leading-relaxed text-ink">
        {children}
      </pre>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(children);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="absolute right-2 top-2 rounded border border-hairline bg-void/80 p-1 text-ink-faint transition-colors hover:text-ink"
        aria-label="Copy"
      >
        <Copy size={12} />
        {copied && <span className="ml-1 text-[10px]">copied</span>}
      </button>
    </div>
  );
}

function CopyHint() {
  return <span>Everything above was tested against this running instance.</span>;
}
